import { useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { useRefetchableQuery } from "core/hooks/useRefetchableQuery";
import {
  GetTaskSchedules_taskSchedulerConnections_places_building as GetTaskSchedulesBuildingGenerated,
  GetTaskSchedules as GetTaskSchedulesGenerated,
  GetTaskSchedules_taskSchedulerConnections_places as GetTaskSchedulesPlaceGenerated,
  GetTaskSchedules_taskSchedulerConnections_places_relatedBookings as GetTaskSchedulesRelatedBookingsGenerated,
  GetTaskSchedules_taskSchedulerConnections_places_relatedTasks as GetTaskSchedulesRelatedTaskGenerated,
  GetTaskSchedulesVariables as GetTaskSchedulesVariablesGenerated,
} from "core/queries/__generated__/GetTaskSchedules";

export enum GetTaskSchedulesType {
  Inquiry = "Inquiry",
  InternalHold = "InternalHold",
  LeaseInquiry = "LeaseInquiry",
  TomBooking = "TomBooking",
}

// GetTaskSchedules exports
export interface GetTaskSchedulesResponse extends GetTaskSchedulesGenerated {}
export interface GetTaskSchedulesVariables
  extends GetTaskSchedulesVariablesGenerated {}
export interface GetTaskSchedulesPlace extends GetTaskSchedulesPlaceGenerated {}
export interface GetTaskSchedulesBuilding
  extends GetTaskSchedulesBuildingGenerated {}
export interface GetTaskSchedulesRelatedBooking
  extends GetTaskSchedulesRelatedBookingsGenerated {}
export interface GetTaskSchedulesRelatedTask
  extends GetTaskSchedulesRelatedTaskGenerated {}

// Queries
export const RelatedTaskScheduleFragment = gql`
  fragment RelatedTaskScheduleFragment on Task {
    id
    assignee {
      avatar: profilePicture {
        id
        url
      }
      id
      firstName
      name
    }
    assignees {
      avatar: profilePicture {
        id
        url
      }
      id
      firstName
      name
    }
    city {
      id
    }
    cleaningRequest {
      id
      booking {
        id
        checkInTime
        checkOutTime
      }
      company {
        id
        name
      }
      category
      shuffled
    }
    dueAt
    estimatedDuration
    earliestStartAt
    latestEndAt
    name
    scheduledStartAt
    startedAt
    completedAt
    status
    subjectType
    tags {
      id
      name
      tagType
    }
    type
    updatedAt
  }
`;

export const getTaskSchedules = gql`
  query GetTaskSchedules(
    $cursor: String = "0"
    $limit: Int = 100
    $filter: TaskSchedulerFilterInput!
    $startAt: RFC3339DateTime!
    $endAt: RFC3339DateTime!
  ) {
    taskSchedulerConnections(
      pagination: { cursor: $cursor, limit: $limit }
      filters: $filter
    ) {
      cursor
      total
      hasMore
      places {
        id
        aptNumber
        aptNumberOrInternalRef
        building {
          id
          internalRef
          name
          shortInternalRef
        }
        brCount
        city {
          id
          name
        }
        cufSpaceType
        internalRef
        nbrBathrooms
        shortInternalRef
        relatedBookings(startAt: $startAt, endAt: $endAt) {
          id
          account {
            id
            firstName
            lastName
            __typename
          }
          bookingUrl
          checkInTime
          checkOutTime
          __typename
        }
        relatedTasks {
          id
          ...RelatedTaskScheduleFragment
        }
      }
    }
  }
  ${RelatedTaskScheduleFragment}
`;

export const getTaskSchedulesStandaloneTask = gql`
  query GetTaskSchedulesStandaloneTask($taskId: ID!) {
    task(id: $taskId) {
      id
      ...RelatedTaskScheduleFragment
    }
  }

  ${RelatedTaskScheduleFragment}
`;

export interface UseGetScheduleTaskQueryOptions {
  notifyOnNetworkStatusChange?: boolean;
  pollInterval?: number;
  skip: boolean;
  variables: GetTaskSchedulesVariables;
}

export const useGetTaskSchedulesQuery = ({
  notifyOnNetworkStatusChange,
  pollInterval,
  skip,
  variables,
}: UseGetScheduleTaskQueryOptions) =>
  useQuery<GetTaskSchedulesResponse, GetTaskSchedulesVariables>(
    getTaskSchedules,
    {
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange,
      pollInterval,
      skip,
      variables,
    },
  );

export const useRefetchableGetTaskSchedulesQuery = ({
  notifyOnNetworkStatusChange,
  pollInterval,
  skip,
  variables,
}: UseGetScheduleTaskQueryOptions) =>
  useRefetchableQuery<GetTaskSchedulesResponse, GetTaskSchedulesVariables>(
    getTaskSchedules,
    {
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange,
      pollInterval,
      skip,
      variables,
    },
  );
